import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { handlingErrors } from '../../utils'
import Api from '../../services/api.service'
import { userLogout } from '../../store/slices/userAuthReducer'
import OutlineButton from '../buttons/OutlineButton'
import GradientButton from '../buttons/GradientButton'

const StyledRadio = styled(Radio)(({ theme }) => ({
  '&.MuiRadio-root': {
    color: '#d1d5db',
    '&.Mui-checked': {
      color: '#667eea',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.04)',
  },
}))

export default function ApplicationModal({ open, onClose, job }) {
  const { t } = useTranslation()
  const user = useSelector((state) => state.userAuth.user)

  const [snapShot, setSnapShot] = useState(null)
  const [cvFile, setCvFile] = useState(null)
  const [motivationMessage, setMotivationMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [existingCVs, setExistingCVs] = useState([])
  const [selectedCVOption, setSelectedCVOption] = useState('upload') // 'upload' or 'existing'
  const [selectedExistingCV, setSelectedExistingCV] = useState('')
  const [loadingCVs, setLoadingCVs] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (open && user) {
      fetchData()
      setSubmitting(false)
    }
  }, [open, user])

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      // Check if file is PDF or DOC/DOCX
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      if (allowedTypes.includes(file.type)) {
        setCvFile(file)
      } else {
        toast.error(t('jobOffers.invalidFileType'), {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: true,
        })
      }
    }
  }

  const handleSubmitApplication = async () => {
    // Validate CV selection
    const hasValidCV =
      (selectedCVOption === 'upload' && cvFile) ||
      (selectedCVOption === 'existing' && selectedExistingCV)

    if (!hasValidCV) {
      toast.error(t('jobOffers.cvRequired'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      })
      return
    }

    if (!motivationMessage.trim()) {
      toast.error(t('jobOffers.motivationRequired'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      })
      return
    }

    setSubmitting(true)
    try {
      let cvDocumentId = null

      // If new document is being uploaded, first add it using EditDocuments logic
      if (selectedCVOption === 'upload' && cvFile) {
        const documentFormData = new FormData()
        documentFormData.append('user', user.data.id)
        documentFormData.append('onlyAdmin', false)
        documentFormData.append('types', ['cv'])
        documentFormData.append('documents[]', cvFile)

        const documentResult = await Api.document.add(documentFormData)
        const documentHandling = handlingErrors(documentResult)

        if (documentHandling.status === 401) {
          dispatch(userLogout())
          setSubmitting(false)
          return
        }

        if (documentHandling.ok) {
          // Get the uploaded document ID from the response
          const uploadedDocuments = documentHandling.data
          cvDocumentId = uploadedDocuments.id
        } else {
          toast.error(t('errorMessage.documentUploadFailed'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
          })
          setSubmitting(false)
          return
        }
      }

      // Now submit the candidature with the document ID using JSON format
      const candidatureData = {
        user: '/api/utilisateurs/' + user.data.id,
        besoinEntreprise: '/api/besoin_entreprises/' + (job.id || job._id),
        motivation: motivationMessage,
        cv: '/api/documents/' + (cvDocumentId || selectedExistingCV),
      }

      const result = await Api.candidature.add(candidatureData)
      const handling = handlingErrors(result)
      if (handling.status === 401) {
        dispatch(userLogout())
        setSubmitting(false)
        return
      }

      if (handling.status === 409) {
        toast.error(t('jobOffers.alreadyApplied'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
        })
        setSubmitting(false)
        return
      }

      if (handling.ok) {
        toast.success(t('jobOffers.applicationSuccess'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
        })
        handleCloseModal()
        setSubmitting(false)
      } else {
        toast.error(t('jobOffers.alreadyApplied'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
        })
        setSubmitting(false)
      }
    } catch (error) {
      console.error('Application submission error:', error)
      toast.error(t('jobOffers.alreadyApplied'), {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
      })
      setSubmitting(false)
    }
  }

  const handleCloseModal = () => {
    onClose()
    setCvFile(null)
    setMotivationMessage('')
    setSelectedCVOption('upload')
    setSelectedExistingCV('')
    setExistingCVs([])
  }

  const fetchData = async () => {
    setLoadingCVs(true)
    try {
      // Fetch user details
      let userResult = await Api.user.detail(user.data.id)
      const userHandling = handlingErrors(userResult)

      if (userHandling.ok) {
        const res = userHandling.data
        setSnapShot(res) // Filter documents where type is 'cv'
        const cvDocuments = res.documents.filter(
          (doc) => doc.type === 'cv' || doc.type === 'Cv'
        )
        setExistingCVs(cvDocuments)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('successMessage.somethingWrong'), {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: true,
      })
    } finally {
      setLoadingCVs(false)
    }
  }

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          p: fullScreen ? 0 : '8px',
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backgroundColor: 'rgba(249, 250, 251, 0.5)',
        }}
      >
        {t('jobOffers.applyForPosition')}
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: '32px !important' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#374151',
            }}
          >
            {job.poste?.titre}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
            }}
          >
            {job.entreprise?.nom || t('contact.company')}
          </Typography>
        </Box>

        {/* CV Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: '#374151',
            }}
          >
            {t('jobOffers.selectCV', 'Sélectionner votre CV')} *
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            {/* Option to upload new CV */}
            <FormControlLabel
              value="upload"
              control={<StyledRadio />}
              label={t('jobOffers.uploadNewCV', 'Télécharger un nouveau CV')}
              checked={selectedCVOption === 'upload'}
              onChange={() => setSelectedCVOption('upload')}
            />
            {/* Option to use existing CV */}
            {existingCVs.length > 0 && (
              <FormControlLabel
                value="existing"
                control={<StyledRadio />}
                label={t('jobOffers.useExistingCV', 'Utiliser un CV existant')}
                checked={selectedCVOption === 'existing'}
                onChange={() => setSelectedCVOption('existing')}
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Existing CVs Selection */}
          {selectedCVOption === 'existing' && existingCVs.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {loadingCVs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <RadioGroup
                  value={selectedExistingCV}
                  onChange={(e) => setSelectedExistingCV(e.target.value)}
                >
                  {existingCVs.map((cv) => (
                    <FormControlLabel
                      key={cv.id}
                      value={cv.id}
                      control={<StyledRadio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cv.nom || cv.filename || `CV_${cv.id}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: '#6b7280' }}
                          >
                            {cv.createdAt &&
                              new Date(cv.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            </Box>
          )}

          {/* New CV Upload */}
          {selectedCVOption === 'upload' && (
            <Box
              sx={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f9fafb',
                '&:hover': {
                  borderColor: '#667eea',
                  backgroundColor: '#f3f4f6',
                },
              }}
            >
              <input
                type="file"
                id="cv-upload"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="cv-upload">
                <Button
                  variant="outlined"
                  component="span"
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.04)',
                      borderColor: '#667eea',
                    },
                  }}
                >
                  {t('jobOffers.chooseFile', 'Choisir un fichier')}
                </Button>
              </label>

              {cvFile && (
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#10b981',
                      fontWeight: 500,
                      flex: 1,
                    }}
                  >
                    {t('jobOffers.fileSelected', 'Fichier sélectionné')}:{' '}
                    {cvFile.name}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setCvFile(null)}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      py: 0.5,
                      borderColor: '#ef4444',
                      color: '#ef4444',
                      '&:hover': {
                        backgroundColor: 'rgba(239, 68, 68, 0.04)',
                        borderColor: '#ef4444',
                      },
                    }}
                  >
                    {t('button.clear', 'Clear')}
                  </Button>
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  color: '#6b7280',
                }}
              >
                {t(
                  'jobOffers.acceptedFormats',
                  'Formats acceptés: PDF, DOC, DOCX'
                )}
              </Typography>
            </Box>
          )}

          {/* Show loading state when fetching existing CVs */}
          {selectedCVOption === 'existing' && loadingCVs && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Show message when no existing CVs are available */}
          {selectedCVOption === 'existing' &&
            !loadingCVs &&
            existingCVs.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" sx={{ color: '#92400e' }}>
                  {t(
                    'jobOffers.noExistingCV',
                    'Aucun CV existant trouvé. Veuillez télécharger un nouveau CV.'
                  )}
                </Typography>
              </Box>
            )}
        </Box>

        {/* Motivation Message */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: '#374151',
            }}
          >
            {t('jobOffers.motivationLetter', 'Lettre de motivation')} *
          </Typography>

          <TextField
            multiline
            rows={6}
            fullWidth
            placeholder={t(
              'jobOffers.motivationPlaceholder',
              'Expliquez pourquoi vous êtes intéressé par ce poste et ce que vous pouvez apporter...'
            )}
            value={motivationMessage}
            onChange={(e) => setMotivationMessage(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          py: 3,
          gap: 2,
          justifyContent: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.8)',
          borderTop: '1px solid rgba(229, 231, 235, 0.8)',
        }}
      >
        <OutlineButton onClick={onClose}>{t('button.cancel')}</OutlineButton>

        <GradientButton onClick={handleSubmitApplication}>
          {submitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {t('profile.saving')}
            </>
          ) : (
            t('button.save')
          )}
        </GradientButton>
      </DialogActions>
    </Dialog>
  )
}
